import { useCallback } from 'react';

import { BackrsUser } from '@backrs_ui/common-ui';

import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { profileApi } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { setUser } from '@backrs_ui/grow-ui/src/redux/user/user';
import {
  useUpdateUserProfileOptionAnswerMutation,
  useUpdateUserSettingsMutation,
} from '@backrs_ui/grow-ui/src/redux/user/user.api';

import { FieldValues, UseFormSetError } from 'react-hook-form';

export const useSettingsFormSubmit = (
  user: BackrsUser | undefined,
  setError: UseFormSetError<FieldValues>,
  setFormSaveError: (error: string | null) => void,
) => {
  const dispatch = useAppDispatch();
  const [updateUser] = useUpdateUserSettingsMutation();
  const [updateUserProfileOptionAnswer] = useUpdateUserProfileOptionAnswerMutation();

  return useCallback(
    async (data: FieldValues) => {
      if (!user) return;

      setFormSaveError(null);

      if (data.newPassword && data.newPassword !== data.confirmPassword) {
        setError('confirmPassword', {
          type: 'manual',
          message: 'Passwords do not match',
        });

        return;
      }

      const updated: BackrsUser = {
        ...user,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        birthday: data.birthday,
        ...(data.newPassword ? { password: data.newPassword } : {}),
      };

      /**
       *
       * HANDLE ZIP CODE (and other profile option answers)
       */
      if (data.zipCode !== user.zip_code) {
        try {
          await updateUserProfileOptionAnswer({
            profileOptionAnswerKey: 'zip_code',
            profileOptionAnswer: {
              user_id: user.id,
              category_id: 'identity',
              question_id: 'zip_code',
              value: data.zipCode,
            },
          }).unwrap();
        } catch (err: unknown) {
          const error = err as { data: { errors: { msg: string }[] } };

          setError('zipCode', {
            type: 'manual',
            message: error?.data?.errors?.[0]?.msg,
          });
        }
      }

      /**
       * HANDLE USER UPDATES (password, first name, last name, email, phone, birthday)
       */
      try {
        const updatedUser: BackrsUser = await updateUser({ user: updated }).unwrap();

        dispatch(setUser({ ...user, ...updatedUser }));
        dispatch(profileApi.util.invalidateTags(['UserProfile']));
      } catch (err) {
        const error = err as {
          data?: {
            errors?: {
              param?: string;
              msg?: string;
            }[];
            message?: string;
          };
        };

        const fieldErrors = error?.data?.errors ?? [];

        fieldErrors.forEach(({ param, msg }) => {
          if (param && typeof param === 'string') {
            setError(param, {
              type: 'manual',
              message: msg || error?.data?.message || 'Invalid input',
            });
          }
        });

        // if the BE returns an error but it doesn't match any of the fields, we will show a generic error
        if (fieldErrors.length === 0) {
          setFormSaveError('Something went wrong and we were unable to save your changes');
        }
      }
    },
    [user, updateUser, updateUserProfileOptionAnswer, dispatch, setError, setFormSaveError],
  );
};
